import { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { usePrefixedNavigate } from "../hooks/usePrefixedNavigate";
import { Button } from "./ui/button";
import { Home, Plus, FileText, Award, MessageSquare } from "lucide-react";
import UserSection from "./UserSection";

interface StudentLayoutProps {
  children: ReactNode;
}

interface MenuItem {
  label: string;
  path: string;
  icon: any;
}

export default function StudentLayout({ children }: StudentLayoutProps) {
  const navigate = usePrefixedNavigate();
  const location = useLocation();

  const menuItems: MenuItem[] = [
    { label: "Dashboard", path: "/student/dashboard", icon: Home },
    { label: "Apply to Hostel", path: "/apply", icon: Plus },
    { label: "My Applications", path: "/applications", icon: FileText },
    { label: "Scholarships", path: "/scholarships", icon: Award },
    { label: "Help", path: "/help", icon: MessageSquare },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="fixed left-0 top-0 flex h-screen w-64 flex-col overflow-hidden border-r border-border bg-card shadow-sm">
        <div className="border-b border-border p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary">
              <Home className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <h2 className="font-bold text-foreground">JBF Portal</h2>
              <p className="text-xs text-muted-foreground">Student Portal</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 space-y-1.5 overflow-y-auto p-4">
          <p className="mb-3 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Menu
          </p>
          {menuItems.map(item => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Button
                key={item.path}
                variant={active ? "default" : "ghost"}
                className={`h-11 w-full justify-start gap-3 ${
                  active
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
                onClick={() => navigate(item.path)}
              >
                <Icon className="h-4 w-4" />
                <span className="font-medium">{item.label}</span>
              </Button>
            );
          })}
        </nav>

        <UserSection portal="student" />
      </aside>

      <div className="ml-64 flex-1">
        <main className="p-6">{children}</main>
      </div>
    </div>
  );
}
